import { useState, useMemo } from "react";
import { SortingState } from "@tanstack/react-table";
import { useNavigate, useLocation } from "react-router-dom";

import { Button } from "ui/button";
import {
  DataGrid,
  TableLoader,
  FillHeightPage,
  TableContainer,
  TableFillHeightBody,
  TableSearchAndFilters,
} from "ui/data-grid";
import { BatchFormDialog } from "containers/admin/clients/client/approval-queue/batch-details/components/batch-form/batch-form-dialog";
import { ConfirmationDialog } from "ui/confirmation-dialog";
import { Breadcrumbs } from "ui/breadcrumbs";

import { useCurrentClient } from "hooks/api/use-clients";
import { useGetBatchStatusesQuery } from "hooks/api/dictionary";
import { useBatches } from "hooks/api/use-batches";

import useBatchActions from "containers/admin/clients/client/approval-queue/hooks/use-batch-actions";

import { Route as AppRoute, getClientPaths } from "utilities/app-routes";

import { createApprovalQueueColumns } from "containers/admin/clients/client/approval-queue/approval-queue-table-columns";

import type { BatchItem } from "containers/admin/clients/client/approval-queue/approval-queue.types";
import type { Unpersisted } from "types/utility";


export const ApprovalQueue = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const client = useCurrentClient();
  const paths = getClientPaths({ clientId: client?.id });

  const { data: batchStatuses = [] } = useGetBatchStatusesQuery();
  const [search, setSearch] = useState<string>("");
  const [isCreateBatchModalOpen, setIsCreateBatchModalOpen] = useState(false);
  const [isDeleteBatchModalOpen, setIsDeleteBatchModalOpen] = useState(false);
  const [batchIdToDelete, setBatchIdToDelete] = useState<string | null>(null);

  // Pagination
  const [pageIndex, setPageIndex] = useState(0);
  const [{ pageSize = 25 }, setAmount] = useState<{
    pageSize: number;
    totalRecords: number;
  }>({
    pageSize: 25,
    totalRecords: 0,
  });

  // Sorting state
  const [sortingParams, setSortingParams] = useState<SortingState>([
    { id: "lastModifiedDate", desc: true },
  ]);

  const sort = useMemo(
    () =>
      sortingParams
        .map((param) => `${param.id},${param.desc ? "desc" : "asc"}`)
        .join(","),
    [sortingParams],
  );

  const { data: batchPage, isLoading: isBatchListLoading } = useBatches({
    clientId: client?.id,
    size: pageSize,
    page: pageIndex,
    sort,
    search,
  });

  const batches = useMemo(() => batchPage?.items ?? [], [batchPage?.items]);
  const isLoading = isBatchListLoading;
  const pagesAmount = batchPage?.totalPages ?? 0;
  const totalRecords = batchPage?.totalItems ?? 0;

  const { handleCreateBatch, handleSendForReview, handleDeleteBatch, isBatchActionPending } =
    useBatchActions({
      clientId: client?.id,
      setIsCreateBatchModalOpen,
    });

  // Create table columns
  const columns = useMemo(
    () =>
      createApprovalQueueColumns({
        pathname: location.pathname,
        batchStatuses,
        onView: (id: string) => {
          const path = `${location.pathname}/${AppRoute.batch}/${id}`;

          navigate(path);
        },
        onSendForReview: handleSendForReview,
        onDelete: (id: string) => {
          setBatchIdToDelete(id);
          setIsDeleteBatchModalOpen(true);
        },
      }),
    [location.pathname, navigate, handleSendForReview, batchStatuses],
  );

  return (
    <FillHeightPage>
      <Breadcrumbs
        breadcrumbs={[
          { label: client?.name, href: paths.clientCampaignsPath },
          { label: "Approval queue", active: true },
        ]}
      />

      <TableContainer fillHeight>
        {(!!batches.length || !!search) && (
          <TableSearchAndFilters
            setCurrentSearchTerm={setSearch}
            actionSlot={
              <Button
                size="large"
                onClick={() => setIsCreateBatchModalOpen(true)}
                aria-label="Add New Batch">
                Create batch
              </Button>
            }
          />
        )}

        <TableFillHeightBody>
          <TableLoader isLoading={isLoading || isBatchActionPending} fullScreen />

          {!isLoading &&
            (batches.length ? (
              <DataGrid
                columns={columns}
                data={batches}
                isLoading={false}
                manualSorting={false}
                showingRecordsIsOn={true}
                viewByAmountSelectIsOn={true}
                totalRecords={totalRecords}
                pageSize={pageSize}
                pagesAmount={pagesAmount}
                setAmount={setAmount}
                pageIndex={pageIndex}
                setPageIndex={(pageIndex) => setPageIndex(pageIndex)}
                sortingParams={sortingParams}
                setSortingParams={setSortingParams}
                fillHeight
              />
            ) : search ? (
              <div className="flex items-center justify-between py-4">
                <p className="text-gray-500">No results found</p>
              </div>
            ) : (
              <div className="flex items-center justify-between py-4">
                <p className="text-gray-500">There are no batches yet</p>
                <Button
                  size="large"
                  onClick={() => setIsCreateBatchModalOpen(true)}
                  aria-label="Add New Batch">
                  Create batch
                </Button>
              </div>
            ))}
        </TableFillHeightBody>
      </TableContainer>
      {/* Create batch modal */}
      {isCreateBatchModalOpen && (
        <BatchFormDialog
          headline="Create batch"
          submitButtonText="Create batch"
          submitLoadingMessage="Creating batch..."
          onClose={() => setIsCreateBatchModalOpen(false)}
          /* The form holds the batch's four editable fields; `useCreateBatch` is typed on the whole
             `Unpersisted<BatchItem>` and sends only what it is given. */
          onSubmit={(values) => handleCreateBatch(values as Unpersisted<BatchItem>)}
          defaultValues={{ name: "", description: "", dueDate: "", mondayBoardLink: "" }}
        />
      )}
      {/* Delete batch modal */}
      {isDeleteBatchModalOpen && (
        <ConfirmationDialog
          open={isDeleteBatchModalOpen}
          onClose={() => setIsDeleteBatchModalOpen(false)}
          intent="warning"
          headline="Are you sure you want to delete this batch?"
          confirmButtonText="Delete batch"
          onConfirm={() => {
            void handleDeleteBatch(batchIdToDelete!);
            setIsDeleteBatchModalOpen(false);
          }}
        />
      )}
    </FillHeightPage>
  );
};

export default ApprovalQueue;
